import React, { useMemo } from 'react';
import type { ActiveKeyMap } from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-registry.tsx';
import Shortcuts from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcuts/index.tsx';
import { useViewMode } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { TOP_OF_CONTAINER, BOTTOM_OF_CONTAINER } from '../../../../model/constants.tsx';
import {
	ASSIGNEE_SHORTCUT_KEY,
	LABELS_SHORTCUT_KEY,
	COMMENT_SHORTCUT_KEY,
} from '../../../../model/keyboard-shortcuts/constants.tsx';
import type { Props } from './types.tsx';

const bentoShortcutKeys = [ASSIGNEE_SHORTCUT_KEY, LABELS_SHORTCUT_KEY, COMMENT_SHORTCUT_KEY];

export const KeyboardShortcuts = ({
	activeIssueId,
	activeIssueKey,
	activeIssueAssignee,
	currentUserAccountId,
	issueKeyForModal,
	hasSwimlanes,
	swimlaneIds,
	onOpenFocusedCard,
	onToggleBentoViewForFocusedCard,
	onSwimlanesToggle,
	onNextIssue,
	onPrevIssue,
	onNextColumn,
	onPrevColumn,
	onOpenAndFocusBentoField,
	onAssignToMe,
	onMoveToTopOrBottomOfContainer,
}: Props) => {
	const viewMode = useViewMode();
	const isDetailViewInSidebar = viewMode === 'SIDEBAR';

	const map = useMemo(() => {
		const keyMap: ActiveKeyMap = {
			...(hasSwimlanes && {
				'-': {
					callback: () => onSwimlanesToggle(swimlaneIds),
				},
			}),
			o: {
				callback: () => onOpenFocusedCard(activeIssueKey),
			},
			t: {
				callback: () =>
					onToggleBentoViewForFocusedCard(activeIssueKey, issueKeyForModal, isDetailViewInSidebar),
			},
			j: {
				callback: onNextIssue,
			},
			k: {
				callback: onPrevIssue,
			},
			n: {
				callback: onNextColumn,
			},
			p: {
				callback: onPrevColumn,
			},
			i: {
				callback: () => onAssignToMe(activeIssueId, activeIssueAssignee, currentUserAccountId),
			},
			's t': {
				callback: () => onMoveToTopOrBottomOfContainer(TOP_OF_CONTAINER),
			},
			's b': {
				callback: () => onMoveToTopOrBottomOfContainer(BOTTOM_OF_CONTAINER),
			},
		};

		bentoShortcutKeys.forEach((shortcutKey) => {
			keyMap[shortcutKey] = {
				callback: () => {
					onOpenAndFocusBentoField(shortcutKey, activeIssueKey);
				},
			};
		});
		return keyMap;
	}, [
		activeIssueAssignee,
		activeIssueId,
		activeIssueKey,
		currentUserAccountId,
		hasSwimlanes,
		isDetailViewInSidebar,
		issueKeyForModal,
		onAssignToMe,
		onMoveToTopOrBottomOfContainer,
		onNextColumn,
		onNextIssue,
		onOpenAndFocusBentoField,
		onOpenFocusedCard,
		onPrevColumn,
		onPrevIssue,
		onSwimlanesToggle,
		onToggleBentoViewForFocusedCard,
		swimlaneIds,
	]);

	return <Shortcuts keyMap={map} location="software.board.keyboard-shortcuts" />;
};
